// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, IconSource, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useNavigationDOM,
  useState,
  useModal,
  usePopover,
  useResponsive,
  useToast,
  useInlineFormMutation,
  ResponsiveType,
  ToastType,
  PopoverType,
  UrlFiltersType,
} from '@supermove/hooks';
import {Job, JobModel, Project, ProjectModel, UserModel} from '@supermove/models';
import {NavigationType} from '@supermove/navigation/src/NavigationTypes';
import {colors, fontWeight, Typography} from '@supermove/styles';
import {openNewTab} from '@supermove/utils';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import Button from '@shared/design/components/Button';
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import EditableField, {EditableFieldProps} from '@shared/design/components/Field/EditableField';
import FieldValue from '@shared/design/components/Field/FieldValue';
import Panel from '@shared/design/components/Panel';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import TextTooltip from '@shared/design/components/TextTooltip';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import FieldValueExpandable from '@shared/modules/Accounting/components/Bill/FieldValueExpandable';
import EditFieldTextAreaButton from '@shared/modules/App/components/EditFieldTextAreaButton';
import CompleteEstimatedRangeJobModal from '@shared/modules/Job/components/CompleteEstimatedRangeJobModal';
import EstimateHoursField from '@shared/modules/Job/components/EstimateHoursField';
import FinalizeJobModal from '@shared/modules/Job/components/FinalizeJobModal';
import JobActionDisabledTooltip from '@shared/modules/Job/components/JobActionDisabledTooltip';
import JobCancelModal from '@shared/modules/Job/components/JobCancelModal';
import JobCompleteManuallyModal from '@shared/modules/Job/components/JobCompleteManuallyModal';
import {handleToggleEstimatedRange} from '@shared/modules/Job/components/JobDateFields';
import JobDispatchStatus from '@shared/modules/Job/components/JobDispatchStatus';
import JobTags from '@shared/modules/Job/components/JobTags';
import ResetJobModal from '@shared/modules/Job/components/ResetJobModal';
import RestoreJobModal from '@shared/modules/Job/components/RestoreJobModal';
import RowJobDateFields from '@shared/modules/Job/components/RowJobDateFields';
import StartTimesField from '@shared/modules/Job/components/StartTimesField';
import UnfinalizeJobModal from '@shared/modules/Job/components/UnfinalizeJobModal';
import JobDateForm from '@shared/modules/Job/forms/JobDateForm';
import JobDescriptionForm from '@shared/modules/Job/forms/JobDescriptionForm';
import JobEstimateHoursForm from '@shared/modules/Job/forms/JobEstimateHoursForm';
import JobStartTimesForm from '@shared/modules/Job/forms/JobStartTimesForm';
import useUpdateJobEstimateHoursMutation from '@shared/modules/Job/hooks/useUpdateJobEstimateHoursMutation';
import useUpdateJobStartTimes from '@shared/modules/Job/hooks/useUpdateJobStartTimes';
import useUpdateProjectJobDatesMutation from '@shared/modules/Job/hooks/useUpdateProjectJobDatesMutation';
import ProjectBlockKind, {
  ProjectBlockKindType,
} from '@shared/modules/Project/enums/ProjectBlockKind';
import useUpdateJobDescriptionMutation from '@shared/modules/Project/hooks/useUpdateJobDescriptionMutation';
import Line from 'modules/App/components/Line';
import MobileProjectBlockHeader from 'modules/Project/V2/Show/Blocks/components/MobileProjectBlockHeader';
import ProjectBlockWrapper from 'modules/Project/V2/Show/Blocks/components/ProjectBlockWrapper';
import {ProjectPageUrlFiltersType} from 'modules/Project/V2/types/ProjectPageUrlFilters';

const Column = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
`;

const DetailButton = Styled.ButtonV2`
`;

const DetailIconContainer = Styled.View`
  width: 16px;
  align-items: center;
`;

const DetailText = Styled.Text`
  ${Typography.Responsive.Body}
`;

const FieldContainer = Styled.View<{responsive: ResponsiveType; isWidget?: boolean}>`
  flex-direction: ${({responsive, isWidget}) => (responsive.desktop && !isWidget ? 'column' : 'row')};
`;

const JobOverviewInfo = Styled.View`
`;

const JobOverviewRow = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const JobName = Styled.Text`
  ${Typography.Responsive.Link}
  ${fontWeight(700)}
  font-size: 16px;
  line-height: 24px;
`;

const DESCRIPTION = 'description';
const OFFICE_NOTES = 'officeNotes';

type SetDetailsType = (newDetails: {[DESCRIPTION]: boolean; [OFFICE_NOTES]: boolean}) => void;

const SkeletonField = ({label}: {label: string}) => {
  return (
    <Column style={{flex: 1}}>
      <FieldValue.LabelText>{label}</FieldValue.LabelText>
      <Space height={8} />
      <SkeletonLoader height={SkeletonLoader.HEIGHT.Text} width={144} />
    </Column>
  );
};

const SkeletonComponent = () => {
  return (
    <Panel>
      <Panel.Header>
        <Column style={{flex: 1}}>
          <Row>
            <Panel.HeaderText>Job Details</Panel.HeaderText>
            <Space style={{flex: 1}} />
            <SkeletonLoader height={SkeletonLoader.HEIGHT.ButtonSmall} width={100} />
          </Row>
          <Space height={12} />
          <Row>
            <SkeletonLoader height={SkeletonLoader.HEIGHT.Text} width={88} />
            <Space width={32} />
            <SkeletonLoader height={SkeletonLoader.HEIGHT.Text} width={88} />
            <Space width={32} />
            <SkeletonLoader height={SkeletonLoader.HEIGHT.Text} width={48} />
          </Row>
        </Column>
      </Panel.Header>
      <Panel.Body>
        <Row style={{flex: 1}}>
          <SkeletonField label={'Job Description'} />
          <SkeletonField label={'Office Notes'} />
        </Row>
      </Panel.Body>
    </Panel>
  );
};

const JobDetailWrapper = ({
  tooltip,
  responsive,
  children,
  isWidget,
}: {
  tooltip?: string;
  responsive: ResponsiveType;
  children: JSX.Element;
  isWidget?: boolean;
}) => {
  if (responsive.desktop && !isWidget) {
    return (
      <TextTooltip isEnabledMobileToast={false} text={tooltip} placement={'top'}>
        {children}
      </TextTooltip>
    );
  }
  return (
    <React.Fragment>
      <Space height={12} />
      {children}
      <Space height={12} />
    </React.Fragment>
  );
};

const JobDetail = ({
  icon,
  tooltip,
  children,
  responsive,
  contentComponent,
  isWidget,
}: {
  icon: IconSource;
  tooltip?: string;
  children?: React.ReactNode;
  responsive: ResponsiveType;
  contentComponent?: JSX.Element;
  isWidget?: boolean;
}) => {
  const isLarge = responsive.desktop && !isWidget;
  return (
    <JobDetailWrapper tooltip={tooltip} responsive={responsive} isWidget={isWidget}>
      <Row>
        <DetailIconContainer>
          <Icon
            source={icon}
            size={isLarge ? 12 : 16}
            color={isLarge ? colors.gray.secondary : colors.blue.interactive}
            style={{marginTop: isLarge ? 3 : 4}}
          />
        </DetailIconContainer>
        <Space width={8} />
        {contentComponent || <DetailText responsive={responsive}>{children}</DetailText>}
      </Row>
    </JobDetailWrapper>
  );
};

const JobDatesV1 = ({
  job,
  responsive,
  isWidget,
}: {
  job: JobModel;
  responsive: ResponsiveType;
  isWidget?: boolean;
}) => {
  return (
    <JobDetail icon={Icon.Calendar} tooltip={'Date(s)'} responsive={responsive} isWidget={isWidget}>
      {Job.getDisplayDate(job, 'MM/DD/YY', 'TBD')}
    </JobDetail>
  );
};

const JobDates = ({job, responsive}: {job: JobModel; responsive: ResponsiveType}) => {
  const jobDate = Job.getDisplayDate(job, 'MM/DD/YY', 'TBD');
  const hasMultipleDates = jobDate.includes(' - ');
  const fieldLabel = hasMultipleDates ? 'Job dates' : 'Job date';
  const {form, handleSubmit} = useUpdateProjectJobDatesMutation({
    jobDateForm: JobDateForm.edit(job),
    onError: (errors) => console.log({errors}),
  });

  const handleSubmitPress = () => {
    const startDate = _.get(form.values, 'jobDateForm.startDate');
    const endDate = _.get(form.values, 'jobDateForm.endDate');
    const isEstimatedRange = _.get(form.values, 'jobDateForm.isEstimatedRange');
    if (isEstimatedRange && startDate && !endDate) {
      handleToggleEstimatedRange({form, field: 'jobDateForm', wasEstimatedRange: true, startDate});
      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const response = await handleSubmit();
            resolve(response);
          } catch (error) {
            reject(error);
          }
        }, 0);
      });
    }
    return handleSubmit();
  };

  return (
    <EditableFieldComponent
      valueProps={{value: jobDate}}
      inputProps={{
        ...form,
        name: 'jobDateForm',
        component: RowJobDateFields,
        input: {
          form,
          setFieldValue: form.setFieldValue,
          field: 'jobDateForm',
          organization: job.organization,
          projectTypeId: job.project.projectTypeId,
        },
        // Errors are handled for individual fields inside RowJobDateFields
        showErrorMessage: false,
      }}
      responseToasts={{displayName: `${fieldLabel} updated`}}
      handleSubmit={handleSubmitPress}
      leftIcon={Icon.Calendar}
      leftIconTooltip={fieldLabel}
      responsive={responsive}
    />
  );
};

const JobArrivalTime = ({
  job,
  responsive,
  isWidget,
}: {
  job: JobModel;
  responsive: ResponsiveType;
  isWidget?: boolean;
}) => {
  return (
    <JobDetail
      icon={Icon.Clock}
      tooltip={'Origin Arrival Window'}
      responsive={responsive}
      isWidget={isWidget}
    >
      {Job.getOriginArrivalWindow(job)}
    </JobDetail>
  );
};

const JobEstimateHoursV1 = ({
  job,
  responsive,
  isWidget,
}: {
  job: JobModel;
  responsive: ResponsiveType;
  isWidget?: boolean;
}) => {
  return (
    <JobDetail
      icon={Icon.CalendarClock}
      tooltip={'Estimate Hours'}
      responsive={responsive}
      isWidget={isWidget}
    >
      {Job.getDisplayEstimateHours(job)}
    </JobDetail>
  );
};

const JobEstimateHours = ({
  job,
  responsive,
  refetch,
}: {
  job: JobModel;
  responsive: ResponsiveType;
  refetch: () => void;
}) => {
  const {form, handleSubmit} = useUpdateJobEstimateHoursMutation({
    jobEstimateHoursForm: JobEstimateHoursForm.edit(job),
    onSuccess: refetch,
    onError: (errors) => console.log({errors}),
  });
  return (
    <EditableFieldComponent
      valueProps={{value: Job.getDisplayEstimateHours(job)}}
      leftIcon={Icon.CalendarClock}
      inputProps={{
        component: EstimateHoursField,
        name: 'jobEstimateHoursForm',
        ...form,
        input: {
          form,
          field: 'jobEstimateHoursForm',
        },
        showErrorMessage: false,
      }}
      leftIconTooltip={'Estimate Hours'}
      responseToasts={{displayName: 'Estimate hours'}}
      handleSubmit={handleSubmit}
      responsive={responsive}
    />
  );
};

const JobOfficeNotes = ({
  job,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  popover,
  responsive,
  isWidget,
}: {
  job: JobModel;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  popover: PopoverType;
  responsive: ResponsiveType;
  isWidget?: boolean;
}) => {
  return (
    <JobDetail
      icon={Icon.StickyNote}
      responsive={responsive}
      isWidget={isWidget}
      contentComponent={
        <OfficeNotesFieldV1
          job={job}
          truncated={truncated}
          setTruncated={setTruncated}
          enabledToggle={enabledToggle}
          setEnabledToggle={setEnabledToggle}
          popover={popover}
          responsive={responsive}
          isWidget={isWidget}
        />
      }
    />
  );
};

const ShareJobPopoverButton = ({
  job,
  moverLinkToast,
  trackingLinkToast,
}: {
  job: JobModel;
  moverLinkToast: ToastType;
  trackingLinkToast: ToastType;
}) => {
  const shareActionsPopover = usePopover({name: 'Share Actions Popover'});

  return (
    <ActionMenuPopover
      popover={shareActionsPopover}
      placement={ActionMenuPopover.Position.BottomEnd}
      actions={Job.getShareActions(job, {moverLinkToast, trackingLinkToast})}
    >
      <SecondaryButton
        text={'Share Job'}
        iconLeft={Icon.Share}
        onPress={shareActionsPopover.handleOpen}
        isSmall
        isDisabled={job.isCancelled}
      />
    </ActionMenuPopover>
  );
};

const PanelHeader = ({
  job,
  refetchAndReset,
  responsive,
  moverLinkToast,
  trackingLinkToast,
}: {
  job: JobModel;
  refetchAndReset: () => void;
  responsive: ResponsiveType;
  moverLinkToast: ToastType;
  trackingLinkToast: ToastType;
}) => {
  const {navigator, params} = useNavigationDOM();

  return (
    <Column style={{flex: 1}}>
      <Row style={{alignItems: 'center'}}>
        <JobDispatchStatus job={job} />
        <Space width={8} />
        <Panel.HeaderText>{Job.getFullName(job)}</Panel.HeaderText>
        <Space style={{flex: 1, minWidth: 12}} />
        <ShareJobPopoverButton
          job={job}
          moverLinkToast={moverLinkToast}
          trackingLinkToast={trackingLinkToast}
        />
        <Space width={16} />
        <JobActionDisabledTooltip job={job}>
          <Button
            text={'Edit Job'}
            iconLeft={Icon.Pen}
            onPress={() =>
              Job.goToEditProjectJobs(job, navigator, {projectUuid: params.projectUuid})
            }
            isSmall
            isDisabled={job.isFinal || job.isCancelled}
          />
        </JobActionDisabledTooltip>
      </Row>
      <Space height={12} />
      <JobTags job={job} refetch={refetchAndReset} />
      <Space height={12} />
      <Row>
        <JobDatesV1 job={job} responsive={responsive} />
        <Space width={32} />
        <JobArrivalTime job={job} responsive={responsive} />
        <Space width={32} />
        <JobEstimateHoursV1 job={job} responsive={responsive} />
      </Row>
    </Column>
  );
};

const EditableFieldComponent = (props: EditableFieldProps & {responsive: ResponsiveType}) => {
  return (
    <EditableField
      isHorizontal
      {...props}
      leftIconProps={{
        color: colors.gray.secondary,
        size: props.responsive.desktop ? 12 : 14,
        style: {marginRight: 4},
        ...props?.leftIconProps,
      }}
      valueProps={{empty: 'None', ...props.valueProps}}
    />
  );
};

const DescriptionField = ({job, responsive}: {job: JobModel; responsive: ResponsiveType}) => {
  const {form, handleSubmit} = useUpdateJobDescriptionMutation({
    jobDescriptionForm: JobDescriptionForm.edit(job.id, job.description),
    onError: (errors) => console.log({errors}),
  });

  return (
    <EditableFieldComponent
      inputProps={{
        ...form,
        name: 'jobDescriptionForm.description',
        input: {multiline: true, style: {minHeight: 136, paddingTop: 8}},
      }}
      handleSubmit={handleSubmit}
      responsive={responsive}
      valueProps={{value: job.description}}
      labelProps={{children: 'Description'}}
      responseToasts={{displayName: 'Job description'}}
      isHorizontal={false}
    />
  );
};

const DescriptionFieldV1 = ({
  job,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  popover,
  responsive,
  isWidget,
}: {
  job: JobModel;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  popover: PopoverType;
  responsive: ResponsiveType;
  isWidget?: boolean;
}) => {
  const {ref, isHovered} = EditFieldTextAreaButton.useHover();
  const {form, submitting, handleSubmit} = useInlineFormMutation({
    name: 'updateJobNotesForm',
    form: {
      jobId: job.id,
      notes: job.description,
    },
    mutation: gql`
      mutation jobDetailsBlockUpdateJobDescription($updateJobNotesForm: UpdateJobNotesForm!) {
        response: updateJobDescription(updateJobNotesForm: $updateJobNotesForm) {
          ${gql.errors}
          job {
            id
            description
          }
        }
      }
    `,
    onSuccess: () => {
      popover.handleClose();
    },
    onError: (errors) => {
      console.log({errors});
    },
  });

  return (
    <FieldContainer
      responsive={responsive}
      style={responsive.desktop && !isWidget ? {flex: 1} : {flexGrow: 1}}
      ref={ref}
      isWidget={isWidget}
    >
      <FieldValueExpandable
        data={job}
        property={DESCRIPTION}
        label={responsive.desktop && !isWidget ? 'Job Description' : ''}
        numberOfLines={5}
        isTruncated={truncated[DESCRIPTION]}
        isEnabledToggle={enabledToggle[DESCRIPTION]}
        setTruncated={setTruncated}
        setEnabledToggle={setEnabledToggle}
        isResponsive
      />
      <EditFieldTextAreaButton
        form={form}
        field={'updateJobNotesForm.notes'}
        handleSubmit={handleSubmit}
        isSubmitting={submitting}
        placeholder={'Enter description'}
        popover={popover}
        buttonLeftOffset={106}
        drawerHeaderText={'Edit Description'}
        isHidden={responsive.desktop && !isHovered}
        isDisabled={job.isCancelled}
      />
    </FieldContainer>
  );
};

const OfficeNotesFieldV1 = ({
  job,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  popover,
  responsive,
  isWidget,
}: {
  job: JobModel;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  popover: PopoverType;
  responsive: ResponsiveType;
  isWidget?: boolean;
}) => {
  const {ref, isHovered} = EditFieldTextAreaButton.useHover();
  const {form, submitting, handleSubmit} = useInlineFormMutation({
    name: 'updateJobNotesForm',
    form: {
      jobId: job.id,
      notes: job.officeNotes,
    },
    mutation: gql`
      mutation jobDetailsBlockUpdateJobOfficeNotes($updateJobNotesForm: UpdateJobNotesForm!) {
        response: updateJobOfficeNotes(updateJobNotesForm: $updateJobNotesForm) {
          ${gql.errors}
          job {
            id
            officeNotes
          }
        }
      }
    `,
    onSuccess: () => {
      popover.handleClose();
    },
    onError: (errors) => {
      console.log({errors});
    },
  });

  const isLarge = responsive.desktop && !isWidget;

  return (
    <FieldContainer responsive={responsive} style={{flex: 1}} ref={ref} isWidget={isWidget}>
      <FieldValueExpandable
        data={job}
        property={OFFICE_NOTES}
        label={isLarge ? 'Office Notes' : ''}
        empty={isLarge ? undefined : 'No office notes'}
        numberOfLines={5}
        isTruncated={truncated[OFFICE_NOTES]}
        isEnabledToggle={enabledToggle[OFFICE_NOTES]}
        setTruncated={setTruncated}
        setEnabledToggle={setEnabledToggle}
        isResponsive
      />
      <EditFieldTextAreaButton
        form={form}
        field={'updateJobNotesForm.notes'}
        handleSubmit={handleSubmit}
        isSubmitting={submitting}
        placeholder={'Enter notes for the office'}
        popover={popover}
        buttonLeftOffset={87}
        drawerHeaderText={'Edit Office Notes'}
        isHidden={isLarge && !isHovered}
        isDisabled={job.isCancelled}
        style={isWidget ? {paddingRight: 2} : undefined}
      />
    </FieldContainer>
  );
};

const OfficeNotesField = ({job, responsive}: {job: JobModel; responsive: ResponsiveType}) => {
  const {form, handleSubmit} = useInlineFormMutation({
    name: 'updateJobNotesForm',
    form: {
      jobId: job.id,
      notes: job.officeNotes,
    },
    mutation: gql`
      mutation jobDetailsBlockUpdateJobOfficeNotes($updateJobNotesForm: UpdateJobNotesForm!) {
        response: updateJobOfficeNotes(updateJobNotesForm: $updateJobNotesForm) {
          ${gql.errors}
          job {
            id
            officeNotes
          }
        }
      }
    `,
    // Success handled automatically
    onSuccess: () => {},
    onError: (errors) => console.log({errors}),
  });

  return (
    <EditableFieldComponent
      inputProps={{
        ...form,
        name: 'updateJobNotesForm.notes',
        input: {multiline: true, style: {minHeight: 136, paddingTop: 8}},
      }}
      leftIcon={Icon.StickyNote}
      leftIconTooltip={'Office notes'}
      valueProps={{value: job.officeNotes}}
      responseToasts={{displayName: 'Office notes'}}
      responsive={responsive}
      handleSubmit={handleSubmit}
      isHorizontal={false}
    />
  );
};

const PanelBody = ({
  job,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  responsive,
}: {
  job: JobModel;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  responsive: ResponsiveType;
}) => {
  const editDescriptionPopover = usePopover({name: 'Edit Description Popover'});
  const editOfficeNotesPopover = usePopover({name: 'Edit Office Notes Popover'});

  return (
    <Row style={{flex: 1}}>
      <DescriptionFieldV1
        key={editDescriptionPopover.key}
        job={job}
        truncated={truncated}
        setTruncated={setTruncated}
        enabledToggle={enabledToggle}
        setEnabledToggle={setEnabledToggle}
        popover={editDescriptionPopover}
        responsive={responsive}
      />
      <Space width={16} />
      <OfficeNotesFieldV1
        key={editOfficeNotesPopover.key}
        job={job}
        truncated={truncated}
        setTruncated={setTruncated}
        enabledToggle={enabledToggle}
        setEnabledToggle={setEnabledToggle}
        popover={editOfficeNotesPopover}
        responsive={responsive}
      />
    </Row>
  );
};

const MobileActionsComponent = ({
  job,
  project,
  navigator,
  params,
  responsive,
  refetchAndReset,
  moverLinkToast,
  trackingLinkToast,
  urlFilters,
  viewer,
  isWidget,
}: {
  job: JobModel;
  project: ProjectModel;
  navigator: NavigationType;
  params: any;
  responsive: ResponsiveType;
  refetchAndReset: () => void;
  moverLinkToast: ToastType;
  trackingLinkToast: ToastType;
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  viewer: UserModel;
  isWidget: boolean;
}) => {
  const actionsMenuPopover = usePopover();
  const completeEstimatedRangeJobModal = useModal({
    name: 'Complete Estimated Range Job Modal',
    enableTracking: true,
  });
  const jobCompleteManuallyModal = useModal({
    name: 'Job Complete Manually Modal',
    enableTracking: true,
  });
  const resetJobModal = useModal({name: 'Reset Job Modal', enableTracking: true});
  const jobCancelModal = useModal({name: 'Cancel Job Modal', enableTracking: true});
  const restoreJobModal = useModal({name: 'Restore Job Modal', enableTracking: true});
  const finalizeJobModal = useModal({name: 'Finalize Job Modal', enableTracking: true});
  const unfinalizeJobModal = useModal({name: 'Unfinalize Job Modal', enableTracking: true});

  return (
    <Column style={{height: '100%'}}>
      <Row style={{alignItems: 'center'}}>
        <JobActionDisabledTooltip job={job}>
          <MobileProjectBlockHeader.TextButton
            text={isWidget ? '' : 'Edit'}
            onPress={() => Job.goToEditProjectJobs(job, navigator, {projectUuid: project.uuid})}
            isDisabled={job.isFinal || job.isCancelled}
          />
        </JobActionDisabledTooltip>
        <Space width={8} />
        <ActionMenuPopover
          popover={actionsMenuPopover}
          width={200}
          actions={Job.getActions(job, {
            responsive,
            navigator,
            projectUuid: params.projectUuid,
            isProjectAtMaxJobs: Project.getIsAtMaxJobs(project),
            userRole: viewer.role,
            onPressMarkAsCompleted: Job.getIsEstimatedRange(job)
              ? completeEstimatedRangeJobModal.handleOpen
              : jobCompleteManuallyModal.handleOpen,
            onPressResetJob: resetJobModal.handleOpen,
            onPressCancelJob: jobCancelModal.handleOpen,
            onPressRestoreJob: restoreJobModal.handleOpen,
            onPressFinalizeJob: finalizeJobModal.handleOpen,
            onPressUnfinalizeJob: unfinalizeJobModal.handleOpen,
            moverLinkToast,
            trackingLinkToast,
          })}
        >
          <MobileProjectBlockHeader.EllipsisButton onPress={actionsMenuPopover.handleToggle} />
        </ActionMenuPopover>
      </Row>
      <CompleteEstimatedRangeJobModal
        key={completeEstimatedRangeJobModal.key}
        jobUuid={job.uuid}
        refetchParent={refetchAndReset}
        isOpen={completeEstimatedRangeJobModal.isOpen}
        handleClose={completeEstimatedRangeJobModal.handleClose}
      />
      <JobCompleteManuallyModal
        key={jobCompleteManuallyModal.key}
        job={job}
        onSuccess={refetchAndReset}
        isOpen={jobCompleteManuallyModal.isOpen}
        handleClose={jobCompleteManuallyModal.handleClose}
      />
      <ResetJobModal
        key={resetJobModal.key}
        moveId={job.moveId}
        refetch={refetchAndReset}
        isOpen={resetJobModal.isOpen}
        handleClose={resetJobModal.handleClose}
      />
      <JobCancelModal
        key={jobCancelModal.key}
        job={job}
        refetch={refetchAndReset}
        isOpen={jobCancelModal.isOpen}
        handleClose={jobCancelModal.handleClose}
        onSuccess={() => urlFilters.handleReset({block: ProjectBlockKind.JOBS})}
      />
      <RestoreJobModal
        key={restoreJobModal.key}
        job={job}
        refetch={refetchAndReset}
        isOpen={restoreJobModal.isOpen}
        handleClose={restoreJobModal.handleClose}
      />
      <FinalizeJobModal
        key={finalizeJobModal.key}
        jobId={job.id}
        refetch={refetchAndReset}
        isOpen={finalizeJobModal.isOpen}
        handleClose={finalizeJobModal.handleClose}
      />
      <UnfinalizeJobModal
        key={unfinalizeJobModal.key}
        jobId={job.id}
        refetch={refetchAndReset}
        isOpen={unfinalizeJobModal.isOpen}
        handleClose={unfinalizeJobModal.handleClose}
      />
    </Column>
  );
};

const MobileDetailButton = ({
  job,
  focusField,
  block,
  children,
}: {
  job: JobModel;
  focusField: string;
  block?: string;
  children: React.ReactNode;
}) => {
  const {navigator, params} = useNavigationDOM();

  return (
    <DetailButton
      onPress={() =>
        Job.goToEditProjectJobs(job, navigator, {
          projectUuid: params.projectUuid,
          block,
          field: focusField,
        })
      }
    >
      {children}
    </DetailButton>
  );
};

const WidgetTitle = ({job, projectUuid}: {job: JobModel; projectUuid: string}) => {
  return (
    <JobOverviewInfo>
      <JobOverviewRow>
        <Space width={8} />
        <TertiaryButton
          onPress={() => openNewTab(`/projects/${projectUuid}/edit/jobs?jobUuid=${job.uuid}`)}
        >
          <JobName>{job.fullName}</JobName>
        </TertiaryButton>
      </JobOverviewRow>
      <Space height={16} />
      <JobDispatchStatus job={job} showLabel />
    </JobOverviewInfo>
  );
};

const MobileJobOverview = ({
  job,
  isWidget,
  editDescriptionPopover,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  responsive,
}: {
  job: JobModel;
  isWidget?: boolean;
  editDescriptionPopover: PopoverType;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  responsive: ResponsiveType;
}) => {
  const Description = (
    <DescriptionFieldV1
      key={editDescriptionPopover.key}
      job={job}
      truncated={truncated}
      setTruncated={setTruncated}
      enabledToggle={enabledToggle}
      setEnabledToggle={setEnabledToggle}
      popover={editDescriptionPopover}
      responsive={responsive}
      isWidget={isWidget}
    />
  );

  if (isWidget) {
    return (
      <React.Fragment>
        <DescriptionField job={job} responsive={responsive} />
        <Space height={16} />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {job.description && (
        <React.Fragment>
          {Description}
          <Space height={16} />
        </React.Fragment>
      )}
      <Row>
        <JobDispatchStatus job={job} showLabel />
      </Row>
      <Space height={16} />
    </React.Fragment>
  );
};

const StartTimesFieldComponent = ({
  job,
  responsive,
}: {
  job: JobModel;
  responsive: ResponsiveType;
}) => {
  const {form, handleSubmit} = useUpdateJobStartTimes({
    jobStartTimesForm: JobStartTimesForm.edit(job),
    onError: (errors) => console.log({errors}),
  });

  return (
    <EditableFieldComponent
      leftIcon={Icon.Clock}
      leftIconTooltip={'Arrival window'}
      valueProps={{value: Job.getOriginArrivalWindow(job)}}
      responseToasts={{displayName: 'Arrival window'}}
      inputProps={{
        ...form,
        showErrorMessage: false,
        name: 'jobStartTimesForm',
        component: StartTimesField,
        input: {organization: job.organization, responsive, form, field: 'jobStartTimesForm'},
      }}
      handleSubmit={handleSubmit}
      responsive={responsive}
    />
  );
};

const MobileProjectBlockContent = ({
  job,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  refetchAndReset,
  refetch,
  responsive,
  isWidget,
  navigator,
  params,
}: {
  job: JobModel;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  refetchAndReset: () => void;
  refetch: () => void;
  responsive: ResponsiveType;
  isWidget?: boolean;
  navigator: NavigationType;
  params: Record<string, string>;
}) => {
  const editDescriptionPopover = usePopover({name: 'Edit Description Popover'});
  const editOfficeNotesPopover = usePopover({name: 'Edit Office Notes Popover'});

  return (
    <React.Fragment>
      <MobileJobOverview
        job={job}
        isWidget={isWidget}
        editDescriptionPopover={editDescriptionPopover}
        truncated={truncated}
        setTruncated={setTruncated}
        enabledToggle={enabledToggle}
        setEnabledToggle={setEnabledToggle}
        responsive={responsive}
      />
      <JobTags job={job} refetch={refetchAndReset} />
      <Space height={12} />
      {isWidget ? (
        <React.Fragment>
          <JobDates job={job} responsive={responsive} />
          <Space height={8} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <MobileDetailButton job={job} focusField={'startDate'}>
            <JobDatesV1 job={job} responsive={responsive} isWidget={isWidget} />
          </MobileDetailButton>
          <Line />
        </React.Fragment>
      )}
      {isWidget ? (
        <React.Fragment>
          <StartTimesFieldComponent job={job} responsive={responsive} />
          <Space height={8} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <MobileDetailButton job={job} focusField={'startTime1'}>
            <JobArrivalTime job={job} responsive={responsive} isWidget={isWidget} />
          </MobileDetailButton>
          <Line />
        </React.Fragment>
      )}
      {isWidget ? (
        <React.Fragment>
          <JobEstimateHours job={job} responsive={responsive} refetch={refetch} />
          <Space height={8} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <MobileDetailButton job={job} focusField={'estimateHours1'}>
            <JobEstimateHoursV1 job={job} responsive={responsive} isWidget={isWidget} />
          </MobileDetailButton>
          <Line />
        </React.Fragment>
      )}
      {isWidget ? (
        <React.Fragment>
          <OfficeNotesField responsive={responsive} job={job} />
        </React.Fragment>
      ) : (
        <MobileDetailButton job={job} focusField={'officeNotes'} block={ProjectBlockKind.Job.NOTES}>
          <JobOfficeNotes
            key={editOfficeNotesPopover.key}
            job={job}
            truncated={truncated}
            setTruncated={setTruncated}
            enabledToggle={enabledToggle}
            setEnabledToggle={setEnabledToggle}
            popover={editOfficeNotesPopover}
            responsive={responsive}
            isWidget={isWidget}
          />
        </MobileDetailButton>
      )}
    </React.Fragment>
  );
};

const JobDetailsBlockContent = ({
  job,
  project,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  refetchAndReset,
  refetch,
  urlFilters,
  viewer,
  isWidget,
}: {
  job: JobModel;
  project: ProjectModel;
  truncated: Record<string, boolean>;
  setTruncated: SetDetailsType;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: SetDetailsType;
  refetchAndReset: () => void;
  refetch: () => void;
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  viewer: UserModel;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();
  const {navigator, params} = useNavigationDOM();
  const moverLinkToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Mover link copied to clipboard!',
  });
  const trackingLinkToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Customer tracking link copied to clipboard!',
  });

  return (
    <React.Fragment>
      {responsive.desktop && !isWidget ? (
        <Panel>
          <Panel.Header>
            <PanelHeader
              job={job}
              refetchAndReset={refetchAndReset}
              responsive={responsive}
              moverLinkToast={moverLinkToast}
              trackingLinkToast={trackingLinkToast}
            />
          </Panel.Header>
          <Panel.Body>
            <PanelBody
              job={job}
              truncated={truncated}
              setTruncated={setTruncated}
              enabledToggle={enabledToggle}
              setEnabledToggle={setEnabledToggle}
              responsive={responsive}
            />
          </Panel.Body>
        </Panel>
      ) : (
        <React.Fragment>
          <MobileProjectBlockHeader
            title={
              isWidget
                ? () => <WidgetTitle job={job} projectUuid={params.projectUuid} />
                : job.displayName
            }
            ActionsComponent={MobileActionsComponent}
            actionsComponentProps={{
              job,
              project,
              navigator,
              params,
              responsive,
              refetchAndReset,
              moverLinkToast,
              trackingLinkToast,
              urlFilters,
              viewer,
              isWidget,
            }}
          />
          <MobileProjectBlockContent
            job={job}
            truncated={truncated}
            setTruncated={setTruncated}
            enabledToggle={enabledToggle}
            setEnabledToggle={setEnabledToggle}
            refetchAndReset={refetchAndReset}
            refetch={refetch}
            responsive={responsive}
            isWidget={isWidget}
            navigator={navigator}
            params={params}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const JobDetailsBlock = ({
  project,
  index,
  jobUuid,
  handleSetPositionY,
  layoutKey,
  refetchAndReset,
  urlFilters,
  projectBlockKind,
  isWidget,
}: {
  project: ProjectModel;
  index: number;
  jobUuid: string;
  handleSetPositionY?: () => void;
  layoutKey?: string;
  refetchAndReset: () => void;
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  projectBlockKind: ProjectBlockKindType;
  isWidget?: boolean;
}) => {
  const [truncated, setTruncated] = useState({[DESCRIPTION]: true, [OFFICE_NOTES]: true});
  const [enabledToggle, setEnabledToggle] = useState({[DESCRIPTION]: false, [OFFICE_NOTES]: false});

  return (
    <ProjectBlockWrapper
      index={index}
      query={JobDetailsBlock.query}
      queryVariables={{jobUuid}}
      layoutKey={layoutKey}
      handleSetPositionY={handleSetPositionY}
      SkeletonComponent={SkeletonComponent}
      projectBlockKind={projectBlockKind}
      isWidget={isWidget}
    >
      {({data, refetch}: {data: {job: JobModel; viewer: UserModel}; refetch: () => void}) => {
        return (
          <JobDetailsBlockContent
            job={data.job}
            project={project}
            truncated={truncated}
            setTruncated={setTruncated}
            enabledToggle={enabledToggle}
            setEnabledToggle={setEnabledToggle}
            refetchAndReset={refetchAndReset}
            refetch={refetch}
            urlFilters={urlFilters}
            viewer={data.viewer}
            isWidget={isWidget}
          />
        );
      }}
    </ProjectBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
JobDetailsBlock.listener = gql`
  fragment JobDetailsBlock_listener on Job {
    id
    calendarPrimaryStatus
  }
`;

JobDetailsBlock.fragment = gql`
  ${Project.goToAddJob.fragment}
  ${Project.getIsAtMaxJobs.fragment}

  fragment JobDetailsBlock on Project {
    id
    ...Project_goToAddJob
    ...Project_getIsAtMaxJobs
  }
`;

JobDetailsBlock.query = gql`
  ${Job.getActions.fragment}
  ${Job.getDisplayDate.fragment}
  ${Job.getFullName.fragment}
  ${JobDispatchStatus.fragment}
  ${Job.getOriginArrivalWindow.fragment}
  ${Job.getDisplayEstimateHours.fragment}
  ${Job.getIsEstimatedRange.fragment}
  ${Job.goToEditProjectJobs.fragment}
  ${JobTags.fragment}
  ${JobActionDisabledTooltip.fragment}
  ${JobCancelModal.fragment}
  ${JobDateForm.edit.fragment}
  ${RowJobDateFields.fragment}
  ${StartTimesField.fragment}
  ${JobStartTimesForm.edit.fragment}
  ${JobEstimateHoursForm.edit.fragment}

  query JobDetailsBlock($jobUuid: String!) {
    ${gql.query}
    viewer {
      id
      role
    }
    job(uuid: $jobUuid) {
      id
      moveId
      uuid
      displayName
      kind
      startTime1
      startTime2
      estimateHours1
      estimateHours2
      description
      officeNotes
      isCancelled
      fullName
      isFinal
      organization {
        id
        slug
        features {
          isEnabledPrintJobCard: isEnabled(feature: "PRINT_JOB_CARD")
        }
        ...RowJobDateFields
        ...StartTimesField
      }
      project {
        id
        projectTypeId
      }
      ...Job_getActions
      ...Job_getDisplayDate
      ...Job_getFullName
      ...JobDispatchStatus
      ...Job_getOriginArrivalWindow
      ...Job_getDisplayEstimateHours
      ...Job_getIsEstimatedRange
      ...Job_goToEditProjectJobs
      ...JobTags
      ...JobActionDisabledTooltip
      ...JobCancelModal
      ...JobDateForm_edit
      ...JobStartTimesForm_edit
      ...JobEstimateHoursForm_edit
    }
  }
`;

export default JobDetailsBlock;
